import dayjs from 'dayjs';
import { useState } from 'react';
import { DISPLAY_DATE_TIME_FORMAT } from 'src/constants/date';
import { useGetDtoolsProjects } from 'src/graphql/entities/integrations/dtools/hooks/use-get-dtools-projects';
import { useGetPortalioProposals } from 'src/graphql/entities/integrations/portalio/hooks/use-get-portalio-proposals';

const PORTALIO_PAGE_SIZE = 20;
const DTOOLS_PAGE_SIZE = 10;

export interface ImportListRow {
  id: number | string;
  name: string;
  description: string;
}

export interface ImportListRows {
  rows: ImportListRow[];
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
}

// The page counter restarts on each new query, so it stays keyed to that query.
const useNextPage = (
  queryKey: string,
  fetchPage: (pageNumber: number) => Promise<unknown>,
) => {
  const [paging, setPaging] = useState({ queryKey, page: 1 });
  const [loadingMore, setLoadingMore] = useState(false);
  const page = paging.queryKey === queryKey ? paging.page : 1;

  const loadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      await fetchPage(nextPage);
      setPaging({ queryKey, page: nextPage });
    } finally {
      setLoadingMore(false);
    }
  };

  return { loadMore, loadingMore };
};

const hasMoreRows = (shown: number, total: number) => shown > 0 && shown < total;

/** portal.io proposals, searched on the rows already loaded, a page of 20 at a time. */
export const usePortalioProposalRows = (searchValue: string): ImportListRows => {
  const {
    proposals = [],
    loading,
    fetchMore,
    total = 0,
  } = useGetPortalioProposals({ pageSize: PORTALIO_PAGE_SIZE, pageNumber: 1, searchValue });
  const { loadMore, loadingMore } = useNextPage(searchValue, (pageNumber) =>
    fetchMore({
      variables: { params: { pageSize: PORTALIO_PAGE_SIZE, searchValue, pageNumber } },
    }),
  );

  const rows = proposals
    .filter(
      (proposal) =>
        proposal.name.toLowerCase().includes(searchValue) ||
        proposal.proposalNumber.toString().includes(searchValue),
    )
    .map((proposal) => ({
      id: proposal.id,
      name: proposal.name,
      description: `#${proposal.proposalNumber} - ${proposal.customer?.firstName} ${proposal.customer?.lastName ?? ''} - ${dayjs(proposal.lastModifiedDate ?? proposal.createdDate).format(DISPLAY_DATE_TIME_FORMAT)}`,
    }));

  return { rows, loading, hasMore: hasMoreRows(rows.length, total), loadingMore, loadMore };
};

/** D-Tools Cloud projects, searched on the server, a page of 10 at a time. */
export const useDtoolsProjectRows = (
  searchValue: string,
  includeArchived: boolean,
): ImportListRows => {
  const {
    projects = [],
    loading,
    fetchMore,
    total = 0,
  } = useGetDtoolsProjects({
    pageSize: DTOOLS_PAGE_SIZE,
    pageNumber: 1,
    searchValue,
    includeArchived,
  });
  const { loadMore, loadingMore } = useNextPage(
    `${includeArchived}:${searchValue}`,
    (pageNumber) =>
      fetchMore({
        variables: {
          params: { searchValue, pageSize: DTOOLS_PAGE_SIZE, pageNumber, includeArchived },
        },
      }),
  );

  const rows = projects.map((project) => ({
    id: project.id,
    name: project.name,
    description: `#${project.number} - ${project.clientName ?? ''} - ${dayjs(project.modifiedDate ?? project.createdDate).format(DISPLAY_DATE_TIME_FORMAT)}${project.isArchived ? ' - Archived' : ''}`,
  }));

  return { rows, loading, hasMore: hasMoreRows(rows.length, total), loadingMore, loadMore };
};
